import { EventSourcePolyfill } from "event-source-polyfill"
import { useState, useEffect, useRef, useCallback } from "react"

export interface SSEOptions {
    withCredentials?: boolean
    autoReconnect?: boolean
    maxReconnectAttempts?: number
    reconnectInterval?: number
    timeout?: number
    startOnMount?: boolean
    headers?: any
}

export interface SSEEventHandlers<T = any> {
    onOpen?: (event: Event) => void
    onMessage?: (data: T, event: MessageEvent) => void
    onError?: (error: Error, event: Event) => void
    onClose?: () => void
    onReconnect?: (attempt: number) => void
    onMaxReconnectAttemptsReached?: () => void
}

export interface UseSSEReturn<T = any> {
    eventSource: EventSource | null
    data: T | null
    error: Error | null
    reconnectCount: number
    readyState: number
    lastEventId: string
    isConnecting: boolean
    isOpen: boolean
    isClosed: boolean
    connect: () => void
    disconnect: () => void
    reconnect: () => void
}

const DEFAULT_OPTIONS: Required<Omit<SSEOptions, "startOnMount" | "headers">> & Pick<SSEOptions, "startOnMount"> = {
    withCredentials: false,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectInterval: 3000,
    timeout: 30000,
    startOnMount: true,
}

export function useSSE<T = any>(
    url: string | null,
    options: SSEOptions = {},
    eventHandlers: SSEEventHandlers<T> = {},
): UseSSEReturn<T> {
    const opts = { ...DEFAULT_OPTIONS, ...options }
    const { onOpen, onMessage, onError, onClose, onReconnect, onMaxReconnectAttemptsReached } = eventHandlers

    const [data, setData] = useState<T | null>(null)
    const [error, setError] = useState<Error | null>(null)
    const [reconnectCount, setReconnectCount] = useState(0)

    // Refs for managing connection lifecycle
    const eventSourceRef = useRef<EventSource | null>(null)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const shouldReconnectRef = useRef<boolean>(true)
    const reconnectAttemptsRef = useRef<number>(0)

    const clearTimeouts = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current)
            connectionTimeoutRef.current = null
        }
    }, [])

    const handleReconnect = useCallback(() => {
        if (!shouldReconnectRef.current || !opts.autoReconnect) return

        if (reconnectAttemptsRef.current >= opts.maxReconnectAttempts) {
            onMaxReconnectAttemptsReached?.()
            return
        }

        reconnectAttemptsRef.current += 1
        setReconnectCount(reconnectAttemptsRef.current)
        onReconnect?.(reconnectAttemptsRef.current)

        reconnectTimeoutRef.current = setTimeout(() => {
            connect()
        }, opts.reconnectInterval)
    }, [
        opts.autoReconnect,
        opts.maxReconnectAttempts,
        opts.reconnectInterval,
        onReconnect,
        onMaxReconnectAttemptsReached,
    ])

    const connect = useCallback(() => {
        if (!url) return

        if (eventSourceRef.current?.readyState === EventSource.OPEN) {
            return
        }

        clearTimeouts()
        shouldReconnectRef.current = true

        try {
            const eventSource = new EventSourcePolyfill(url, {
                withCredentials: opts.withCredentials,
                headers: opts.headers
            })

            eventSourceRef.current = eventSource

            if (opts.timeout > 0) {
                connectionTimeoutRef.current = setTimeout(() => {
                    if (eventSource.readyState === EventSource.CONNECTING) {
                        eventSource.close()
                        const timeoutError = new Error(`Connection timeout after ${opts.timeout}ms`)
                        setError(timeoutError)
                        onError?.(timeoutError, new Event("timeout"))
                    }
                }, opts.timeout)
            }

            eventSource.onopen = (event) => {
                clearTimeouts()
                reconnectAttemptsRef.current = 0
                setReconnectCount(0)
                setError(null)
                onOpen?.(event)
            }

            eventSource.onmessage = (event) => {
                let parsedData: T
                try {
                    parsedData = JSON.parse(event.data)
                } catch {
                    parsedData = event.data as T
                }

                setData(parsedData)
                onMessage?.(parsedData, event)
            }

            eventSource.onerror = (event) => {
                clearTimeouts()
                if (eventSource.readyState === EventSource.CLOSED) {
                    const error = new Error("SSE connection error")
                    setError(error)
                    onError?.(error, event)

                    if (shouldReconnectRef.current) {
                        handleReconnect()
                    }
                }
            }
        } catch (err) {
            const connectionError = err instanceof Error ? err : new Error("Failed to create SSE connection")
            setError(connectionError)
            onError?.(connectionError, new Event("connection-failed"))
        }
    }, [url, opts.withCredentials, opts.timeout, clearTimeouts, handleReconnect, onOpen, onMessage, onError])

    const disconnect = useCallback(() => {
        shouldReconnectRef.current = false
        clearTimeouts()

        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }

        onClose?.()
    }, [clearTimeouts, onClose])

    const reconnect = useCallback(() => {
        reconnectAttemptsRef.current = 0
        setReconnectCount(0)
        disconnect()
        setTimeout(connect, 100)
    }, [disconnect, connect])

    useEffect(() => {
        if (url && opts.startOnMount) {
            connect()
        }

        return () => {
            disconnect()
        }
    }, [url, opts.startOnMount, connect, disconnect])

    const eventSource = eventSourceRef.current
    const readyState = eventSource?.readyState ?? EventSource.CLOSED
    // @ts-ignore
    const lastEventId = eventSource?.id ?? ""

    return {
        eventSource,
        data,
        error,
        reconnectCount,
        readyState,
        lastEventId,
        isConnecting: readyState === EventSource.CONNECTING,
        isOpen: readyState === EventSource.OPEN,
        isClosed: readyState === EventSource.CLOSED,
        connect,
        disconnect,
        reconnect,
    }
}

export default useSSE
